import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { SidebarNavItem } from '@/components/SidebarNavRail/types';
import { LocalStorageKeys } from '@/constants/localStorage';

/**
 * How Dugout offers navigation.
 *
 * `select` is the `<select>` Dugout shipped with and stays the default, so nobody is moved off it
 * without asking; `drawer` is the menu behind the top bar's hamburger, opted into from settings.
 * `drawer` also keeps the top bar up on wide viewports, so the account menu sits top right at
 * every width; see GlobalNavHeader.
 */
export type NarrowNavStyle = 'drawer' | 'select';

/** What a mounted section rail publishes for the narrow layout to re-use. */
export interface SectionNav {
  /** Names the section for assistive tech, e.g. "Person" or "Billing". */
  ariaLabel: string;
  /** What the section is called — a person's name, "Billing". Plain text, for use as a label. */
  title: string;
  /** Section branding — a person's avatar and name, a section title. */
  header: React.ReactNode;
  items: SidebarNavItem[];
  /** Label of the entry that owns the current route. */
  currentLabel?: string;
}

export interface NarrowNavContextInterface {
  navStyle: NarrowNavStyle;
  setNavStyle: (style: NarrowNavStyle) => void;
  /** The section whose rail is mounted, or null when the route is not inside one. */
  section: SectionNav | null;
  /**
   * Publishes the open section's links. Shaped as a ref-style callback: a rail passes itself
   * while mounted and `null` on the way out.
   */
  registerSection: (section: SectionNav | null) => void;
  /** Whether the narrow layout's menu is out. */
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  /** True on a wide viewport, where the rails are the navigation and the menu is not offered. */
  wide: boolean;
}

/** Tailwind's `lg`, where the dropdown navigation's rails appear. */
const WIDE_QUERY = '(min-width: 1024px)';

const isWide = () => {
  try {
    return window.matchMedia(WIDE_QUERY).matches;
  } catch {
    return false;
  }
};

const NarrowNavContext = createContext<NarrowNavContextInterface | undefined>(undefined);

const readNavStyle = (): NarrowNavStyle => {
  try {
    return localStorage.getItem(LocalStorageKeys.NARROW_NAV_STYLE) === 'drawer'
      ? 'drawer'
      : 'select';
  } catch {
    // Private browsing and embedded contexts can throw on access; default to the dropdown.
    return 'select';
  }
};

/**
 * A section rail re-renders whenever anything on the page around it does, and its `header` is a
 * fresh element every time. Comparing what actually drives the menu — which section, which links,
 * which one is current — keeps that churn from re-rendering the drawer, and lets a rail publish
 * itself on every render without a dependency list to keep in step.
 *
 * `header` is deliberately not compared: it only ever changes alongside the links (a different
 * person means different `/people/:id` paths), so it can ride along on those updates.
 */
const sameSection = (a: SectionNav | null, b: SectionNav | null) =>
  a === b ||
  (!!a &&
    !!b &&
    a.ariaLabel === b.ariaLabel &&
    a.title === b.title &&
    a.currentLabel === b.currentLabel &&
    a.items === b.items);

/**
 * Holds the narrow layout's navigation: which style of it the user has asked for, and the links
 * of whichever section they are inside.
 *
 * The section rails are the one place that already knows a section's links and which of them owns
 * the route, and they mount at every width (the rail markup is simply hidden below `lg`), so the
 * drawer reads its section level from them rather than assembling a second copy.
 */
export const NarrowNavProvider = ({ children }: { children: React.ReactNode }) => {
  const [navStyle, setNavStyleState] = useState<NarrowNavStyle>(readNavStyle);
  const [section, setSection] = useState<SectionNav | null>(null);
  const [wide, setWide] = useState(isWide);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const handleChange = () => setWide(query.matches);

    query.addEventListener('change', handleChange);

    return () => query.removeEventListener('change', handleChange);
  }, []);

  const setNavStyle = useCallback((style: NarrowNavStyle) => {
    setNavStyleState(style);

    try {
      localStorage.setItem(LocalStorageKeys.NARROW_NAV_STYLE, style);
    } catch {
      // Losing the preference is not worth breaking navigation over.
    }
  }, []);

  /*
   * Published on `<body>` for the `menu-nav` CSS variant, the way the theme publishes `.dark`: the
   * pinned bands under the top bar have to clear it on wide viewports too, and they are spread
   * across pages that otherwise have no reason to know which navigation someone picked.
   */
  useEffect(() => {
    document.body.dataset.navStyle = navStyle;

    return () => {
      delete document.body.dataset.navStyle;
    };
  }, [navStyle]);

  const registerSection = useCallback(
    (next: SectionNav | null) =>
      setSection((current) => (sameSection(current, next) ? current : next)),
    []
  );

  const value = useMemo(
    () => ({
      navStyle,
      setNavStyle,
      section,
      registerSection,
      menuOpen,
      setMenuOpen,
      wide
    }),
    [menuOpen, navStyle, registerSection, section, setNavStyle, wide]
  );

  return <NarrowNavContext.Provider value={value}>{children}</NarrowNavContext.Provider>;
};

/**
 * Returns the narrow nav state. Falls back to an inert dropdown-style registry when no provider is
 * mounted, so rails and pages stay renderable in isolation (tests, Storybook).
 */
export const useNarrowNav = (): NarrowNavContextInterface => {
  const context = useContext(NarrowNavContext);

  return (
    context ?? {
      navStyle: 'select',
      setNavStyle: () => {},
      section: null,
      registerSection: () => {},
      menuOpen: false,
      setMenuOpen: () => {},
      wide: false
    }
  );
};

export default NarrowNavContext;
